"use client"

/**
 * Shell — the full app chrome (TopBar, Sidebar, Tabs, MiniApp, Chat panel).
 *
 * Wraps children in the shell UI and sets up:
 * - DAG sync WebSocket connection
 * - Legacy patch message handling
 * - Tab management from URL
 *
 * Used by AppLayout when shell mode is active (?shell=true or shellDefault).
 * Always requires an authenticated user — AppLayout handles the auth gate.
 */

import React, { Suspense, useEffect, useRef, useCallback } from 'react';

import { TopBar } from '@sourcepilot/ui/shell/nav/top-bar';
import { SimpleSidebar } from '@sourcepilot/ui/shell/sidebar/simple-sidebar';
import { TabArea } from '@sourcepilot/ui/shell/layout/tab-area';
import { TabContentArea } from '@sourcepilot/ui/shell/layout/tab-content-area';
import { MiniAppArea } from '@sourcepilot/ui/shell/sidebar/mini-app';
import { TooltipProvider } from '@sourcepilot/ui/framework/ui/atoms/tooltip';

import { useAtom } from 'jotai';
import { useSimpleAuth as useAuth } from '@sourcepilot/ui/shell/auth/use-simple-auth';
import { patchStateAtom, patchStepsAtom, patchThreadsAtom, emptyPatchState, useTabData, parseUrlToTab } from '@sourcepilot/ui/framework/state/session-data';
import { useDagSyncSocket } from '@sourcepilot/ui/framework/data/hooks/use-dag-sync-socket';
import { useSearchParams, usePathname } from 'next/navigation';

function ShellContent({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { tabs, addTab } = useTabData();
  const isInitialLoadRef = useRef(true);

  // DAG sync WebSocket management via Jotai
  const {
    connect: connectDagSync,
    isConnected,
    hasEverConnected,
    registerMessageHandler,
  } = useDagSyncSocket();

  // socket patch updates
  const [, setPatchState] = useAtom(patchStateAtom);
  const [, setPatchSteps] = useAtom(patchStepsAtom);
  const [, setPatchThreads] = useAtom(patchThreadsAtom);

  // Track if sidebar chat is open (determined by ?chat= param)
  const chatParam = searchParams.get('chat');
  const chatOpenRef = useRef(!!chatParam);
  useEffect(() => {
    chatOpenRef.current = !!chatParam;
  }, [chatParam]);

  // WebSocket message handler
  const on_dag_sync_message = useCallback(
    (data: string | object) => {
      const data_obj = typeof data === 'string' ? JSON.parse(data) : data;

      if (data_obj.type === 'workspace-patch' || (data_obj.ids && !data_obj.type)) {
        const patch_obj = data_obj;
        patch_obj.ids = new Set<string>(patch_obj.ids || []);
        setPatchState(patch_obj);
      } else {
        if (data_obj.threadId !== undefined && data_obj.role !== undefined) {
          if (!chatOpenRef.current) return;
          setPatchSteps((prevSteps: any) => [...prevSteps, data_obj]);
        } else if (
          data_obj.name !== undefined &&
          data_obj.description !== undefined &&
          !data_obj.threadId
        ) {
          setPatchThreads((prevThreads: any) => [...prevThreads, data_obj]);
        }
      }
    },
    [setPatchState, setPatchSteps, setPatchThreads],
  );

  // Initialize DAG sync WebSocket connection when user is available
  useEffect(() => {
    if (user && !isConnected) {
      registerMessageHandler(on_dag_sync_message);
      connectDagSync(on_dag_sync_message);
    }
  }, [user, isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create tab from URL if no tab matches the current path
  // On initial load: always create. On navigation: only if no matching tab exists.
  useEffect(() => {
    if (!pathname) return;
    const isInitial = isInitialLoadRef.current;
    isInitialLoadRef.current = false;

    // Check if any existing tab already matches this path
    if (!isInitial && tabs.length > 0) {
      const hasMatch = tabs.some(tab =>
        pathname === tab.pathname || pathname.startsWith(tab.pathname + '/')
      );
      if (hasMatch) return;
    }

    const tabData = parseUrlToTab(pathname);
    if (!tabData) return;
    addTab(tabData);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col w-full h-full">
      <TooltipProvider>
        <TopBar />
        {/* the live connection dropped (a deploy, a restart, the network):
            say so while it reconnects, so a stale page is never silent */}
        {user && hasEverConnected && !isConnected && (
          <div
            role="status"
            data-testid="connection-lost"
            className="bg-amber-100 px-3 py-1 text-center text-xs text-amber-900"
          >
            Connection lost — reconnecting… Changes made elsewhere will appear once it is back.
          </div>
        )}
        <div className="flex flex-1 overflow-hidden">
          <SimpleSidebar />
          <MiniAppArea />
          <div className="flex flex-col flex-grow ml-[1px] overflow-hidden">
            <Suspense fallback={<div className="h-10 bg-gray-100" />}>
              <TabArea />
            </Suspense>
            <Suspense
              fallback={
                <div className="flex-1 flex items-center justify-center">Loading...</div>
              }
            >
              <TabContentArea defaultChatWidth={600}>{children}</TabContentArea>
            </Suspense>
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-screen">Loading...</div>
      }
    >
      <ShellContent>{children}</ShellContent>
    </Suspense>
  );
}
